import {
  DepositCaptureStrategy,
  type IDepositSettings,
  type ITreatmentTemplate,
} from '@principle-theorem/principle-core/interfaces';
import { LegacyDepositValueNormalization } from '@principle-theorem/principle-core';
import { DAYS_OF_WEEK, type WithRef } from '@principle-theorem/shared';
import { type Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DepositTreatmentTemplateFormGroup } from './deposit-treatment-template.formgroup';

export interface IDepositTreatmentTemplateRow {
  readonly template: WithRef<ITreatmentTemplate>;
  readonly form: DepositTreatmentTemplateFormGroup;
}

export interface IDepositTreatmentTemplateGroup {
  readonly label: string;
  readonly rows: IDepositTreatmentTemplateRow[];
  readonly bookableOnline: boolean;
}

interface ICreateDepositTreatmentTemplateRowsOptions {
  readonly templates: WithRef<ITreatmentTemplate>[];
  readonly settings?: IDepositSettings;
  readonly markDirty: () => void;
  readonly onDestroy$: Observable<void>;
}

interface IDepositTreatmentTemplateRows {
  readonly publicRows: IDepositTreatmentTemplateRow[];
  readonly privateRows: IDepositTreatmentTemplateRow[];
  readonly groups: IDepositTreatmentTemplateGroup[];
}

/**
 * Builds the treatment-template deposit row collection so every display group
 * shares one consistent form setup and lifecycle subscription arrangement.
 */
export class DepositTreatmentTemplateRows {
  /**
   * Creates the editable treatment-template rows from stored settings so form
   * state preserves legacy overrides and reports user changes to the parent.
   */
  static create({
    templates,
    settings,
    markDirty,
    onDestroy$,
  }: ICreateDepositTreatmentTemplateRowsOptions): IDepositTreatmentTemplateRows {
    const rows = templates.map((template) => {
      const override = settings?.treatmentTemplateOverrides?.[template.ref.id];
      const depositRequired = override?.depositRequired ?? false;
      const amount =
        override?.depositRequired === true ? override.amount : undefined;
      const inheritedCaptureStrategy =
        LegacyDepositValueNormalization.captureStrategy(
          settings?.captureStrategy ??
            DepositCaptureStrategy.ChargeOnBookingRequest
        );
      const captureStrategy =
        override?.depositRequired === true
          ? LegacyDepositValueNormalization.captureStrategy(
              override.captureStrategy ?? inheritedCaptureStrategy
            )
          : inheritedCaptureStrategy;
      const inheritsCaptureStrategy =
        override?.depositRequired === true &&
        override.captureStrategy === undefined;
      const appliesTo =
        override?.depositRequired === true ? override.appliesTo ?? [] : [];
      const depositRequiredDays =
        override?.depositRequired === true
          ? override.depositRequiredDays ?? [...DAYS_OF_WEEK]
          : [...DAYS_OF_WEEK];
      const form = new DepositTreatmentTemplateFormGroup({
        depositRequired,
        amount,
        captureStrategy,
        inheritsCaptureStrategy,
        appliesTo,
        depositRequiredDays,
      });

      form.controls.depositRequired.valueChanges
        .pipe(takeUntil(onDestroy$))
        .subscribe(() => form.updateAmountValidators());
      form.controls.depositRequired.valueChanges
        .pipe(takeUntil(onDestroy$))
        .subscribe((required) => {
          if (required) {
            form.controls.amountValue.markAsTouched();
          }
        });
      form.valueChanges.pipe(takeUntil(onDestroy$)).subscribe(() => {
        if (form.dirty) {
          markDirty();
        }
      });
      return { template, form };
    });
    const publicRows = rows.filter((row) => row.template.isPublic);
    const privateRows = rows.filter((row) => !row.template.isPublic);

    return {
      publicRows,
      privateRows,
      groups: [
        {
          label: 'Available for booking online',
          rows: publicRows,
          bookableOnline: true,
        },
        {
          label: 'Only available for booking by staff',
          rows: privateRows,
          bookableOnline: false,
        },
      ],
    };
  }
}
